import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../Utils/LoadingAnimations/TableLoadingSkeleton';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import LazyImage from './Components/imageComponent';
import { globalEnv } from '../../../config/baseConfig';
import ErrorAnim from '../../Utils/ErrorAnimations/queryErrorAnim';
import { setSearchPlaceholderAction } from '../../../redux/actions/searchBarAction';
import { axiosInstance } from '../../../config/axiosInstance';

async function fetchDashboards(page: number, token: string) {
	const config = {
		headers: {
			Authorization: 'Bearer ' + token
		}
	};
	const { data } = await axiosInstance.get(
		`${globalEnv.BASE_URL}/admin/dashboard/get-all?page=${page}`,
		config
	);
	return data;
}

export default function DashboardTable() {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const { isError, data, isFetching, isLoading } = useQuery(
		['dashboards', page],
		() => fetchDashboards(page, ACCESS_TOKEN),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000
		}
	);

	const dispatch = useDispatch();
	dispatch(setSelectedSection('Dashboard Documents'));
	dispatch(setSearchPlaceholderAction('Search for Dashboards'));

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['dashboards', page + 1], () =>
				fetchDashboards(page + 1, ACCESS_TOKEN)
			);
		}
	}, [data, page, queryClient]);

	return (
		<section>
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light global-color-transition">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data[0].data.map((item: any) => (
										<TableData
											key={item._id}
											id={item._id}
											name={item.title}
											username={item.user.username}
											userId={item.user._id}
											createdDate={item.createdAt}
											selectedTopics={item.selectedTopics}
											photo={
												item.user.photo != null
													? item.photo
													: `https://api.dicebear.com/7.x/bottts/svg?seed=${item.username}`
											}
										/>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<ErrorAnim />
				) : (
					<Pagination
						itemsPerPage={data[0].meta.limit}
						totalCount={data[0].meta.total}
						totalPages={data[0].meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

const TableHeaders = () => {
	return (
		<tr>
			<th className="p-2 whitespace-nowrap dark:text-white">
				<div className="font-semibold text-left">Name</div>
			</th>
			<th className="p-2 whitespace-nowrap dark:text-white">
				<div className="font-semibold text-left">Dashboard user</div>
			</th>
			<th className="p-2 whitespace-nowrap dark:text-white">
				<div className="font-semibold text-center">Creation Date</div>
			</th>
			<th className="p-2 whitespace-nowrap dark:text-white">
				<div className="font-semibold text-center">Topics</div>
			</th>
		</tr>
	);
};

type tableDateType = {
	id: string;
	name: string;
	username: string;
	userId: string;
	createdDate: string;
	selectedTopics: number;
	photo: string;
};

const TableData = ({
	id,
	name,
	username,
	userId,
	createdDate,
	selectedTopics,
	photo
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../dashboard/${id}`);
			}}
		>
			<td className="col-span-1 p-2 break-all w-1/4">
				<div className="font-medium text-left text-gray-800 dark:text-white">
					{name}
				</div>
			</td>
			<td className="col-span-1 p-2 break-all w-1/3">
				<div className="flex items-center">
					<div
						className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3"
						onClick={(e) => {
							e.stopPropagation();
							navigate(`../user/${userId}`);
						}}
					>
						<LazyImage photo={photo} />
					</div>
					<div className="font-medium dark:text-gray-400">{username}</div>
				</div>
			</td>
			<td className="p-2 whitespace-nowrap w-1/5">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(createdDate).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
			<td className="p-2 whitespace-nowrap w-1/4">
				<div className="font-medium text-center dark:text-gray-400">
					{selectedTopics}
				</div>
			</td>
		</tr>
	);
};
